import { useEffect, useState, type ReactNode } from "react"

import { BsFunnelFill } from "react-icons/bs"
import moment from "moment"

import SearchableMultiSelect from "./SearchableMultiSelect"
import SearchableSelect from "./SearchableSelect"

import { Badge } from "../ui/badge"

import { Button } from "../ui/button"

import {
  DateRangePicker,
  dateRangeStringsToRange,
  dateRangeToStrings,
} from "../ui/date-range-picker"

import { Label } from "../ui/label"

const FILTER_FIELD_CLASSNAME = "h-9 rounded-md"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip"

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer"

import { cn } from "../../lib/utils"
import type { DateRange } from "react-day-picker"

export type GridColumnFilterOption =
  | string
  | {
      value: string
      label: string
    }

export type GridColumnFilterField = {
  key: string
  label: string
  options: GridColumnFilterOption[]
  type?: "select" | "dateRange"
}

export function normalizeGridColumnFilterOptions(
  options: GridColumnFilterOption[],
): Array<{ value: string; label: string }> {
  return options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  )
}

export function parseDateRangeFilterValues(values: string[] | null | undefined) {
  if (!values?.length) return { from: "", to: "" }
  if (values.length === 1) {
    return { from: values[0], to: "" }
  }
  const [from = "", to = ""] = values
  return { from, to }
}

export function buildDateRangeFilterValues(from: string, to: string): string[] | null {
  const start = from.trim()
  const end = to.trim()
  if (!start && !end) return null
  return [start, end]
}

export function formatDateRangeFilterLabel(values: string[] | null | undefined): string {
  const { from, to } = parseDateRangeFilterValues(values)
  const format = (value: string) =>
    moment(value, "YYYY-MM-DD", true).isValid()
      ? moment(value, "YYYY-MM-DD").format("DD MMM YYYY")
      : value

  if (from && to) {
    return from === to ? format(from) : `${format(from)} – ${format(to)}`
  }
  if (from) return `From ${format(from)}`
  if (to) return `Until ${format(to)}`
  return ""
}

type GridColumnFilterPanelBaseProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  fields: GridColumnFilterField[]
  onClear: () => void
}

type GridColumnFilterPanelSingleProps = GridColumnFilterPanelBaseProps & {
  multiple?: false
  values: Record<string, string | null>
  onApply: (values: Record<string, string | null>) => void
}

type GridColumnFilterPanelMultiProps = GridColumnFilterPanelBaseProps & {
  multiple: true
  values: Record<string, string[] | null>
  onApply: (values: Record<string, string[] | null>) => void
}

type GridColumnFilterPanelProps =
  | GridColumnFilterPanelSingleProps
  | GridColumnFilterPanelMultiProps

function fieldHasSingleValue(value: string | null | undefined): boolean {
  return Boolean(value)
}

function fieldHasMultiValue(value: string[] | null | undefined): boolean {
  return Boolean(value?.length)
}

function DateRangeFilterEditor({
  value,
  onChange,
  label,
}: {
  value: string[] | null | undefined
  onChange: (value: string[] | null) => void
  label: string
}) {
  const { from, to } = parseDateRangeFilterValues(value)
  const range = dateRangeStringsToRange(from, to)

  const handleChange = (next?: DateRange) => {
    const [nextFrom, nextTo] = dateRangeToStrings(next)
    onChange(buildDateRangeFilterValues(nextFrom, nextTo))
  }

  return (
    <div className="space-y-3">
      <DateRangePicker
        value={range}
        onChange={handleChange}
        placeholder={`All ${label.toLowerCase()}`}
        numberOfMonths={1}
        popoverPortal={false}
      />
      <p className="text-xs text-muted-foreground">
        Select a start date, end date, or both.
      </p>
    </div>
  )
}

function GridColumnFilterPanelSingle({
  open,
  onOpenChange,
  fields,
  values,
  onApply,
  onClear,
}: GridColumnFilterPanelSingleProps) {
  const [draft, setDraft] = useState(values)
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setDraft(values)
      setSelectedFieldKey((current) => {
        if (current && fields.some((field) => field.key === current)) {
          return current
        }
        return fields[0]?.key ?? null
      })
    }
  }, [open, values, fields])

  const selectedField =
    fields.find((field) => field.key === selectedFieldKey) ?? fields[0] ?? null

  const handleApply = () => {
    onApply(draft)
    onOpenChange(false)
  }

  const handleClear = () => {
    const cleared = Object.fromEntries(fields.map((field) => [field.key, null]))
    setDraft(cleared)
    onClear()
    onOpenChange(false)
  }

  return (
    <GridColumnFilterPanelLayout
      open={open}
      onOpenChange={onOpenChange}
      fields={fields}
      onSelectField={setSelectedFieldKey}
      fieldHasValue={(fieldKey) => fieldHasSingleValue(draft[fieldKey])}
      onClear={handleClear}
      onApply={handleApply}
      selectedField={selectedField}
      editor={
        selectedField ? (
          <SearchableSelect
            value={draft[selectedField.key] ?? ""}
            className={FILTER_FIELD_CLASSNAME}
            menuPortal={false}
            options={[
              {
                value: "",
                label: `All ${selectedField.label.toLowerCase()}`,
              },
              ...normalizeGridColumnFilterOptions(selectedField.options),
            ]}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                [selectedField.key]: value || null,
              }))
            }
            placeholder={`All ${selectedField.label.toLowerCase()}`}
            searchPlaceholder={`Search ${selectedField.label.toLowerCase()}…`}
            aria-label={selectedField.label}
          />
        ) : null
      }
    />
  )
}

function GridColumnFilterPanelMulti({
  open,
  onOpenChange,
  fields,
  values,
  onApply,
  onClear,
}: GridColumnFilterPanelMultiProps) {
  const [draft, setDraft] = useState(values)
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setDraft(values)
      setSelectedFieldKey((current) => {
        if (current && fields.some((field) => field.key === current)) {
          return current
        }
        return fields[0]?.key ?? null
      })
    }
  }, [open, values, fields])

  const selectedField =
    fields.find((field) => field.key === selectedFieldKey) ?? fields[0] ?? null

  const handleApply = () => {
    onApply(draft)
    onOpenChange(false)
  }

  const handleClear = () => {
    const cleared = Object.fromEntries(fields.map((field) => [field.key, null]))
    setDraft(cleared)
    onClear()
    onOpenChange(false)
  }

  return (
    <GridColumnFilterPanelLayout
      open={open}
      onOpenChange={onOpenChange}
      fields={fields}
      onSelectField={setSelectedFieldKey}
      fieldHasValue={(fieldKey) => fieldHasMultiValue(draft[fieldKey])}
      onClear={handleClear}
      onApply={handleApply}
      selectedField={selectedField}
      editor={
        selectedField ? (
          selectedField.type === "dateRange" ? (
            <DateRangeFilterEditor
              label={selectedField.label}
              value={draft[selectedField.key]}
              onChange={(value) =>
                setDraft((prev) => ({
                  ...prev,
                  [selectedField.key]: value,
                }))
              }
            />
          ) : (
            <SearchableMultiSelect
              value={draft[selectedField.key] ?? []}
              className={FILTER_FIELD_CLASSNAME}
              menuPortal={false}
              options={normalizeGridColumnFilterOptions(selectedField.options)}
              onChange={(value) =>
                setDraft((prev) => ({
                  ...prev,
                  [selectedField.key]: value.length ? value : null,
                }))
              }
              placeholder={`All ${selectedField.label.toLowerCase()}`}
              searchPlaceholder={`Search ${selectedField.label.toLowerCase()}…`}
              aria-label={selectedField.label}
            />
          )
        ) : null
      }
    />
  )
}

type GridColumnFilterPanelLayoutProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  fields: GridColumnFilterField[]
  onSelectField: (fieldKey: string) => void
  fieldHasValue: (fieldKey: string) => boolean
  onClear: () => void
  onApply: () => void
  selectedField: GridColumnFilterField | null
  editor: ReactNode
}

function GridColumnFilterPanelLayout({
  open,
  onOpenChange,
  fields,
  onSelectField,
  fieldHasValue,
  onClear,
  onApply,
  selectedField,
  editor,
}: GridColumnFilterPanelLayoutProps) {
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction="right"
      shouldScaleBackground={false}
    >
      <DrawerContent
        className={cn(
          "inset-x-auto inset-y-0 right-0 mt-0 flex h-full w-[40rem] max-w-[40rem] flex-col gap-0 rounded-none rounded-l-[10px] border-l p-0",
          "[&>div:first-child]:hidden",
        )}
      >
        <DrawerHeader className="border-b border-border px-6 py-5 text-left">
          <DrawerTitle>Column filters</DrawerTitle>
          <DrawerDescription>
            Narrow the grid by any column value.
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex min-h-0 flex-1">
          <div className="flex w-44 shrink-0 flex-col border-r border-border">
            <div className="border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Fields
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {fields.map((field) => {
                const isSelected = selectedField?.key === field.key
                const hasValue = fieldHasValue(field.key)

                return (
                  <button
                    key={field.key}
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors",
                      isSelected
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-foreground hover:bg-muted/60",
                    )}
                    onClick={() => onSelectField(field.key)}
                  >
                    <span className="truncate">{field.label}</span>
                    {hasValue && (
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                        aria-hidden
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
            {selectedField ? (
              <div className="space-y-2">
                <Label>{selectedField.label}</Label>
                {editor}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No filterable columns available.
              </p>
            )}
          </div>
        </div>

        <DrawerFooter className="border-t border-border px-6 py-4 sm:flex-row sm:justify-between">
          <Button type="button" variant="ghost" onClick={onClear}>
            Clear all
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={onApply}>
              Apply filters
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export function GridColumnFilterPanel(props: GridColumnFilterPanelProps) {
  if (props.multiple) {
    return <GridColumnFilterPanelMulti {...props} />
  }

  return <GridColumnFilterPanelSingle {...props} />
}

type GridColumnFilterButtonProps = {
  activeCount: number
  onClick: () => void
  disabled?: boolean
  className?: string
  label?: string
}

export function GridColumnFilterButton({
  activeCount,
  onClick,
  disabled = false,
  className,
  label = "Filters",
}: GridColumnFilterButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn("relative h-9 w-9 shrink-0", className)}
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
        >
          <BsFunnelFill className="h-4 w-4" aria-hidden />
          {activeCount > 0 && (
            <Badge
              variant="secondary"
              className="absolute -right-1.5 -top-1.5 h-4 min-w-4 justify-center rounded-full px-1 text-[9px] font-semibold leading-none"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

export function collectUniqueStringValues(
  rows: Array<Record<string, unknown>>,
  field: string,
): string[] {
  const values = new Set<string>()
  rows.forEach((row) => {
    const list = row[field]
    if (!Array.isArray(list)) return
    list.forEach((item) => {
      if (typeof item === "string" && item.trim()) values.add(item)
    })
  })
  return Array.from(values).sort((a, b) => a.localeCompare(b))
}

export function collectUniqueScalarValues<TRow>(
  rows: TRow[],
  getValue: (row: TRow) => string,
): string[] {
  const values = new Set<string>()
  rows.forEach((row) => {
    const value = getValue(row).trim()
    if (value) values.add(value)
  })
  return Array.from(values).sort((a, b) => a.localeCompare(b))
}
